import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ClientStatementLine } from '@garage/shared'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters } from '@/lib/hooks.js'
import { Spinner } from '@/ui'

/**
 * Computes the payment status for a statement line.
 */
function paymentStatus(line: ClientStatementLine): 'paid' | 'partial' | 'unpaid' {
  if (line.balanceMinor === 0) return 'paid'
  if (line.balanceMinor > 0 && line.amountPaidMinor > 0) return 'partial'
  return 'unpaid'
}

/**
 * Printable client statement: a plain HTML/CSS page (no Tailwind, no app chrome) so
 * anyone can open this file and tweak fonts, spacing or the logo size
 * without touching React or the design system.
 *
 * Opened in its own tab from the client detail page. Print with Ctrl/Cmd+P;
 * the .no-print button and page-frame around it disappear in @media print.
 */
export function ClientStatementPrintView() {
  const { id } = useParams<{ id: string }>()
  const pageRef = useRef<HTMLDivElement>(null)
  const [downloading, setDownloading] = useState(false)
  const { t } = useTranslation()
  const formatters = useFormatters()

  const { data: statement, isLoading } = useQuery({
    queryKey: qk.clientStatement(id ?? ''),
    queryFn: () => api.clients.statement(id ?? ''),
    enabled: !!id,
  })

  const { data: settings } = useQuery({
    queryKey: qk.settings,
    queryFn: () => api.settings.get(),
  })

  if (isLoading || !statement) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size="md" />
      </div>
    )
  }

  const handleDownloadPdf = async () => {
    if (!pageRef.current) return
    setDownloading(true)
    try {
      const html2pdf = (await import('html2pdf.js')).default
      await html2pdf()
        .set({
          filename: `statement-${statement.client.name.replace(/[^a-z0-9]+/gi, '-')}.pdf`,
          margin: 0,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(pageRef.current)
        .save()
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <style>{PRINT_CSS}</style>

      <div className="no-print toolbar">
        <button className="toolbar-button" onClick={() => window.print()}>
          {t('common.print')}
        </button>
        <button className="toolbar-button" onClick={handleDownloadPdf} disabled={downloading}>
          {downloading ? t('jobCardPrint.preparingPdf') : t('jobCardPrint.downloadPdf')}
        </button>
      </div>

      <div className="sheet">
        <div className="page" ref={pageRef}>
          <header className="header">
            {settings && (
              <img
                className="logo"
                src={api.settings.logoUrl()}
                alt=""
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}
            <div className="header-text">
              <div className="business-name">{settings?.businessName || 'Cypek Garage'}</div>
              <h1 className="title">{t('clientStatement.title')}</h1>
            </div>
            <div className="header-meta">
              <div>
                <span className="label">{t('clientStatement.generatedOn')}</span> {formatters.date(statement.generatedAt)}
              </div>
            </div>
          </header>

          <section className="box">
            <h2>{t('clientStatement.billTo')}</h2>
            <div className="row">
              <span className="label">{t('jobCardPrint.name')}</span>
              <span>{statement.client.name}</span>
            </div>
            <div className="row">
              <span className="label">{t('clients.columns.phone')}</span>
              <span>{statement.client.phone || '—'}</span>
            </div>
            {statement.client.trn && (
              <div className="row">
                <span className="label">{t('clientDetail.taxRegistrationNumber')}</span>
                <span>{statement.client.trn}</span>
              </div>
            )}
            {statement.client.tradeLicence && (
              <div className="row">
                <span className="label">{t('clientDetail.tradeLicence')}</span>
                <span>{statement.client.tradeLicence}</span>
              </div>
            )}
            {statement.client.billingAddress && (
              <div className="row">
                <span className="label">{t('clientDetail.billingAddress')}</span>
                <span>{statement.client.billingAddress}</span>
              </div>
            )}
          </section>

          {statement.lines.length === 0 ? (
            <p className="muted">{t('clientStatement.noInvoicesYet')}</p>
          ) : (
            <>
              <table className="ledger">
                <thead>
                  <tr>
                    <th>{t('clientStatement.columns.status')}</th>
                    <th>{t('clientStatement.columns.date')}</th>
                    <th>{t('clientStatement.columns.invoice')}</th>
                    <th>{t('clientStatement.columns.jobcard')}</th>
                    <th className="num">{t('clientStatement.columns.total')}</th>
                    <th className="num">{t('clientStatement.columns.paid')}</th>
                    <th className="num">{t('clientStatement.columns.balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.lines.map((line) => (
                    <tr key={line.jobcardId}>
                      <td>
                        <span className={`badge badge-${paymentStatus(line)}`}>
                          {t(`clientStatement.status.${paymentStatus(line)}`)}
                        </span>
                      </td>
                      <td>{formatters.date(line.date)}</td>
                      <td>{line.invoiceNumber ?? '—'}</td>
                      <td>{line.number}</td>
                      <td className="num">{formatters.money(line.totalMinor)}</td>
                      <td className="num">{formatters.money(line.amountPaidMinor)}</td>
                      <td className="num">{formatters.money(line.balanceMinor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="box">
                <div className="summary-row">
                  <span>{t('clientStatement.totalInvoiced')}</span>
                  <span>{formatters.money(statement.totals.totalInvoicedMinor)}</span>
                </div>
                <div className="summary-row">
                  <span>{t('clientStatement.totalPaid')}</span>
                  <span>{formatters.money(statement.totals.totalPaidMinor)}</span>
                </div>
                <div className="summary-row total">
                  <span>{t('clientStatement.totalOutstanding')}</span>
                  <span>{formatters.money(statement.totals.totalOutstandingMinor)}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * Plain CSS, deliberately not Tailwind: this is the file to open when the
 * printed layout needs to change. Edit colours, spacing or the page size
 * here directly.
 */
const PRINT_CSS = `
  @page {
    size: A4;
    margin: 16mm;
  }

  /* Always white paper, never the app's dark theme. */
  .sheet {
    color-scheme: light;
    background: #fff;
    min-height: 100vh;
  }

  .page {
    max-width: 190mm;
    margin: 0 auto;
    padding: 16px;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
  }

  .toolbar {
    position: fixed;
    top: 16px;
    right: 16px;
    display: flex;
    gap: 8px;
  }
  .toolbar-button {
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background: #111;
    border: none;
    border-radius: 6px;
    cursor: pointer;
  }
  .toolbar-button:hover { background: #333; }
  .toolbar-button:disabled { background: #888; cursor: wait; }

  .header {
    display: flex;
    align-items: center;
    gap: 16px;
    border-bottom: 3px solid #111;
    padding-bottom: 12px;
    margin-bottom: 16px;
  }
  .logo { height: 56px; width: auto; object-fit: contain; }
  .header-text { flex: 1; }
  .business-name { font-size: 13px; color: #555; }
  .title {
    margin: 2px 0 0;
    font-size: 26px;
    letter-spacing: 2px;
    text-transform: uppercase;
  }
  .header-meta { text-align: right; font-size: 13px; line-height: 1.6; }

  .box {
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 16px;
  }
  .box h2 {
    margin: 0 0 8px;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #555;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid #eee;
    font-size: 14px;
  }
  .row:last-child { border-bottom: none; }

  .label {
    font-weight: 600;
    color: #555;
    min-width: 80px;
  }

  .muted { color: #888; font-style: italic; margin: 0; }

  .ledger {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    margin-bottom: 16px;
  }
  .ledger th, .ledger td {
    padding: 8px 10px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }
  .ledger th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #555;
    border-bottom: 2px solid #ccc;
  }
  .ledger td.num, .ledger th.num { text-align: right; }
  .summary-row {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    font-size: 14px;
  }
  .summary-row.total {
    font-weight: 700;
    font-size: 16px;
    border-top: 2px solid #111;
    padding-top: 10px;
    margin-top: 6px;
  }

  .badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }
  .badge-paid { background: #e6f4ea; color: #1e7e34; }
  .badge-partial { background: #fff4e5; color: #b56a00; }
  .badge-unpaid { background: #fdecea; color: #c0392b; }

  @media print {
    .no-print { display: none !important; }
    .page { padding: 0; max-width: none; }
    .box { break-inside: avoid; }
  }
`
